import axios from 'axios'

import configs from '@acre/config'

import {
  GeneralInsuranceInput,
  GeneralInsuranceProvider,
  GetGeneralInsuranceProductsInput,
  GetGeneralInsuranceQuoteResponse,
  GetGeneralInsuranceQuotesResponse,
  InputMaybe,
  Maybe,
} from '../generated/resolvers'
import request from '../requesters/default'
import giRequest from '../requesters/generalInsurance'
import {
  AcreProcessorAddGeneralInsuranceProductCaseBody,
  CdmCreateGeneralInsuranceProductRequest,
} from '../service/luther/model'
import { setVerificationCacheInvalidation } from '../utils/caseVerificationInvalidation'

const { CLIENT_PORTAL_API_URL, CLIENT_PORTAL_OATHKEEPER_API_URL } = configs

export enum GiProvider {
  Uinsure = 'UINSURE',
  Paymentshield = 'PAYMENTSHIELD',
}

export type GiCheckAuthBody = {
  encrypted_username: string
  encrypted_password: string
  provider: GeneralInsuranceProvider
}

export const createGeneralInsuranceProduct = async (input: CdmCreateGeneralInsuranceProductRequest) => {
  const response = await request.post('/general_insurance_product', input)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

export const updateGeneralInsuranceProduct = async (id: string, input: CdmCreateGeneralInsuranceProductRequest) => {
  const response = await request.patch(`/general_insurance_product/${id}`, input)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

export const addGeneralInsuranceProductCase = async (
  general_insurance_product_id: string,
  input: AcreProcessorAddGeneralInsuranceProductCaseBody,
) => {
  const response = await request.post(`/general_insurance_product/${general_insurance_product_id}/case`, input)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

export const removeGeneralInsuranceProduct = async (general_insurance_product_id: string, case_id: string) => {
  const response = await request.delete(`/general_insurance_product/${general_insurance_product_id}/case/${case_id}`)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

export const getGeneralInsuranceProduct = async (
  input: InputMaybe<GetGeneralInsuranceProductsInput>,
  useClientApi: Maybe<boolean>,
) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const baseApiUrl = useOathKeeper === 'true' ? CLIENT_PORTAL_OATHKEEPER_API_URL : CLIENT_PORTAL_API_URL
  const axiosOpts = useClientApi ? { baseURL: `${baseApiUrl}`, params: input } : { params: input }

  const response = await request.get('/general_insurance_product', axiosOpts)
  return response?.data
}

export const fetchQuotes = async (input: InputMaybe<GeneralInsuranceInput>) => {
  const response = await giRequest.post<GetGeneralInsuranceQuotesResponse>('/quotes', input)
  return response?.data
}

export const fetchQuote = async (case_id: string, quote_id: string) => {
  const response = await giRequest.get<GetGeneralInsuranceQuoteResponse>(`/quotes/${case_id}/${quote_id}`)
  return response?.data
}

export const checkGeneralInsuranceQuotesExist = async (case_id: string, quote_ids: string[]) => {
  const promises = quote_ids.map((quote_id) =>
    giRequest.get<GetGeneralInsuranceQuoteResponse>(`/quotes/${case_id}/${quote_id}`),
  )

  const giQuotesResponse = await Promise.allSettled(promises)

  // assume gi quote exists if the request failed for any reason that is not 404
  return giQuotesResponse
    .map((res, idx) => {
      if (res.status === 'fulfilled') return quote_ids[idx]

      const rejectReason = res.reason
      if (axios.isAxiosError(rejectReason) && rejectReason.response?.status === 404) return null
      return quote_ids[idx]
    })
    .filter(Boolean) as string[]
}

export const checkAuthGi = async (body: GiCheckAuthBody): Promise<{ credentials_valid: boolean } | undefined> => {
  const response = await giRequest.post('/check_auth', body)
  return response?.data
}
